import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onClick
}: FAQItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-87">
      <button onClick={onClick} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-88">
        <span className="text-lg font-semibold text-[var(--jet-black)] pr-4" data-id="element-89">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-[var(--viridia-green)] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-90" />
      </button>
      <AnimatePresence data-id="element-91">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-92">
            <div className="px-6 pb-5 text-[var(--graphite-gray)] leading-relaxed" data-id="element-93">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'How long does setup take?',
    answer: "Setup takes 48-72 hours after you complete our onboarding form. We handle everything—from connecting your platforms to configuring your AI responses and calendar. You'll be up and running within 3 business days."
  }, {
    question: "What if I don't have a CRM yet?",
    answer: "No problem! We include access to Viridia's white-label CRM as part of your package. It's pre-configured and ready to track your leads, appointments, and customer pipeline from day one."
  }, {
    question: 'Can I add more platforms later?',
    answer: "Yes! The $297/month price includes ONE platform (Yelp, Facebook, or Instagram). If you want to add additional platforms, we can do that for an additional monthly fee. Just let us know and we'll send you pricing."
  }, {
    question: 'Can I switch platforms later?',
    answer: "Yes, absolutely! You can switch from one platform to another (for example, from Yelp to Facebook) at any time. Just let us know and we'll reconfigure your AI auto-responder within 24 hours at no extra charge."
  }, {
    question: 'Do I need technical skills to use this?',
    answer: 'Not at all. This is a completely done-for-you service. We handle all the technical setup, configuration, and ongoing maintenance. You just watch the appointments roll in and focus on delivering great service.'
  }, {
    question: 'How does this work with my current marketing?',
    answer: 'This service works best when paired with local advertising (Google Ads, Facebook Ads, local SEO). The automation ensures that every lead from your marketing gets an instant response and booking opportunity—maximizing your ad spend ROI. Many clients see 3-5x better conversion rates when combining paid ads with our instant response system.'
  }, {
    question: 'Can I customize the AI responses?',
    answer: "Yes! During onboarding, we'll work with you to customize your AI's tone, messaging, and responses to match your brand voice. You can request adjustments anytime through our support team."
  }, {
    question: 'Can I cancel anytime?',
    answer: 'Yes! The automation service (AI responder, booking calendar, CRM) can be cancelled anytime. However, if you choose to run advertising campaigns through us, those agreements require a 6-month minimum term to ensure optimal campaign performance and ROI.'
  }];
  return <section className="py-20 px-6 bg-[var(--white-smoke)]" data-id="element-94">
      <div className="max-w-4xl mx-auto" data-id="element-95">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-96">
          <h2 className="text-4xl md:text-5xl font-bold text-[var(--jet-black)] mb-4" data-id="element-97">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-[var(--graphite-gray)]" data-id="element-98">
            Everything you need to know before getting started
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-99">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} data-id="element-100" />)}
        </div>
      </div>
    </section>;
}